import gql from 'graphql-tag'

/**
 * Mutation to create a new vehicle list for a given workspace.
 * Requires workspaceId, name, and type in the input.
 */
export const CREATE_VEHICLE_LIST_M = gql`
  mutation CreateVehicleList(
    $workspaceId: String!
    $cameraId: String
    $name: String!
    $type: VehicleListTypeEnum!
  ) {
    createVehicleList(
      createVehicleListInput: {
        workspaceId: $workspaceId
        cameraId: $cameraId
        name: $name
        type: $type
      }
    ) {
      code
      success
      message
      vehicleList {
        id
        workspaceId
        cameraId
        name
        type
        createdAt
        updatedAt
      }
    }
  }
`

/**
 * Mutation to snapshot an existing vehicle list into a new list with a given name.
 * Creates a point-in-time copy of the source vehicle list's records.
 */
export const SNAPSHOT_VEHICLE_LIST_M = gql`
  mutation SnapshotVehicleList(
    $sourceVehicleListId: Int!
    $newName: String!
  ) {
    snapshotVehicleList(
      snapshotVehicleListInput: {
        sourceVehicleListId: $sourceVehicleListId
        newName: $newName
      }
    ) {
      code
      success
      message
      vehicleList {
        id
        workspaceId
        cameraId
        name
        type
        createdAt
        updatedAt
      }
    }
  }
`

/**
 * Mutation to import vehicle records into a manual vehicle list from a Base64-encoded CSV file.
 */
export const IMPORT_VEHICLE_RECORDS_M = gql`
  mutation ImportVehicleRecords($vehicleListId: Int!, $csvBase64: String!) {
    importVehicleRecordsFromCsv(
      importVehicleRecordsInput: { vehicleListId: $vehicleListId, csvBase64: $csvBase64 }
    ) {
      code
      success
      message
      importedCount
    }
  }
`

export const DELETE_VEHICLE_LIST_M = gql`
  mutation DeleteVehicleList($id: String!) {
    deleteVehicleList(id: $id) {
      success
    }
  }
`

export const EDIT_DERIVED_VEHICLE_LIST_M = gql`
  mutation EditDerivedVehicleList(
    $listId: String!
    $timeRangeType: String!
    $timeRangePastRange: Float
    $timeRangePastUnit: String
    $timeRangeFrom: DateTime
    $timeRangeTo: DateTime
    $appearanceCountEnable: Boolean!
    $appearanceCountLowerBound: Float
    $make: [String!]
    $type: [String!]
    $stateCode: [String!]
    $skipReplaceInConditions: Boolean
  ) {
    editDerivedVehicleList(
      editDerivedVehicleListDTO: {
        listId: $listId
        timeRange: {
          type: $timeRangeType
          pastRange: $timeRangePastRange
          pastUnit: $timeRangePastUnit
          from: $timeRangeFrom
          to: $timeRangeTo
        }
        appearanceCount: {
          enable: $appearanceCountEnable
          lowerBound: $appearanceCountLowerBound
        }
        make: $make
        type: $type
        stateCode: $stateCode
        skipReplaceInConditions: $skipReplaceInConditions
      }
    ) {
      vehicleListId
      success
    }
  }
`

/**
 * Mutation to derive a new vehicle list from an existing one based on specified filter criteria.
 * This mutation explicitly defines all arguments inline for better transparency on what it takes.
 */
export const CREATE_VIP_AUDIENCE_M = gql`
  mutation CreateVipAudience(
    $cameraId: String!
    $timeRangeType: String!
    $timeRangePastRange: Float
    $timeRangePastUnit: String
    $timeRangeFrom: DateTime
    $timeRangeTo: DateTime
    $appearanceCountEnable: Boolean!
    $appearanceCountLowerBound: Float
  ) {
    createVipAudience(
      createVipAudienceDTO: {
        cameraId: $cameraId
        timeRange: {
          type: $timeRangeType
          pastRange: $timeRangePastRange
          pastUnit: $timeRangePastUnit
          from: $timeRangeFrom
          to: $timeRangeTo
        }
        appearanceCount: {
          enable: $appearanceCountEnable
          lowerBound: $appearanceCountLowerBound
        }
      }
    ) {
      vehicleListId
      success
    }
  }
`

export const DERIVE_VEHICLE_LIST_M = gql`
  mutation DeriveVehicleList(
    $originalVehicleListIds: [String!]!
    $newVehicleListName: String!
    $timeRangeType: String!
    $timeRangePastRange: Float
    $timeRangePastUnit: String
    $timeRangeFrom: DateTime
    $timeRangeTo: DateTime
    $appearanceCountEnable: Boolean!
    $appearanceCountLowerBound: Float
    $make: [String!]
    $type: [String!]
    $stateCode: [String!]
  ) {
    deriveVehicleList(
      deriveVehicleListDTO: {
        originalVehicleListIds: $originalVehicleListIds
        newVehicleListName: $newVehicleListName
        timeRange: {
          type: $timeRangeType
          pastRange: $timeRangePastRange
          pastUnit: $timeRangePastUnit
          from: $timeRangeFrom
          to: $timeRangeTo
        }
        appearanceCount: {
          enable: $appearanceCountEnable
          lowerBound: $appearanceCountLowerBound
        }
        make: $make
        type: $type
        stateCode: $stateCode
      }
    ) {
      vehicleListId
      success
    }
  }
`
